<template>
  <div ref="rootEl" class="location-mention relative">
    <div class="relative">
      <div
        ref="editorEl"
        v-bind="editableAttrs"
        role="textbox"
        :contenteditable="isDisabled ? 'false' : 'true'"
        :placeholder="placeholderText"
        :data-placeholder="placeholderText"
        :aria-label="placeholderText"
        :style="{ minHeight }"
        class="location-mention-editor surface focus:border-secondary focus:ring-secondary disabled:dark:bg-surface/5! block max-h-full w-full rounded-md border-none p-1 break-words whitespace-pre-wrap select-text empty:before:pointer-events-none empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)] focus:ring-2 sm:py-1.5 sm:text-sm"
        :class="forwardedClass"
        @input="handleInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @click="handleEditorClick"
        @dblclick="handleEditorDblClick"
        @focusin="isFocused = true"
        @focusout="handleFocusout"
      ></div>
      <slot name="overlay" />
    </div>

    <ul
      v-if="open"
      data-testid="location-dropdown"
      class="surface absolute right-0 left-0 z-40 max-h-64 overflow-auto rounded-md py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
      :style="{ top: selectorTop + 'px' }"
    >
      <template v-if="selectorMode === 'at'">
        <li v-if="loading && atItems.length === 0" class="px-2 py-1 text-gray-500">Zoeken…</li>
        <li v-else-if="atItems.length === 0" class="px-2 py-1 text-gray-500">
          {{
            atMentionMode === "project"
              ? "Geen projecten gevonden"
              : atMentionMode === "business"
                ? "Geen bedrijven gevonden"
                : "Geen resultaten gevonden"
          }}
        </li>
        <li
          v-for="(item, index) in atItems"
          :key="item.key"
          data-testid="at-mention-suggestion"
          :data-mention-mode="item.mode"
          :data-highlighted="index === highlightedIndex ? 'true' : 'false'"
          class="hover:bg-secondary/10 flex cursor-pointer items-center gap-x-2 px-2 py-1 select-none"
          :class="index === highlightedIndex ? 'bg-secondary/10' : ''"
          @mouseenter="highlightedIndex = index"
          @mousedown.prevent="selectAtSuggestion(item)"
        >
          <span class="text-primary inline-block w-12 shrink-0 text-[10px] tracking-wide uppercase">
            {{
              item.mode === "project" ? "Project" : item.mode === "business" ? "Bedrijf" : "Persoon"
            }}
          </span>
          <span class="truncate">{{ item.label }}</span>
        </li>
      </template>

      <template v-else>
        <li v-if="trimmedQuery.length < MIN_CHARS" class="px-2 py-1 text-gray-500">
          Typ / of druk twee keer op spatie om een locatie te zoeken. Gebruik minimaal
          {{ MIN_CHARS }} tekens.
        </li>
        <li v-else-if="loading && items.length === 0" class="px-2 py-1 text-gray-500">Zoeken…</li>
        <li v-else-if="items.length === 0" class="px-2 py-1 text-gray-500">
          Geen locaties gevonden
        </li>
        <li
          v-for="(item, index) in items"
          :key="item.key"
          data-testid="location-suggestion"
          :data-source="item.source"
          :data-highlighted="index === highlightedIndex ? 'true' : 'false'"
          class="hover:bg-secondary/10 flex cursor-pointer items-center gap-x-2 px-2 py-1 select-none"
          :class="index === highlightedIndex ? 'bg-secondary/10' : ''"
          @mouseenter="highlightedIndex = index"
          @mousedown.prevent="selectSuggestion(item)"
        >
          <span
            class="inline-block w-12 shrink-0 text-[10px] tracking-wide uppercase"
            :class="item.source === 'saved' ? 'text-secondary' : 'text-gray-400'"
          >
            {{ item.source === "saved" ? "Opgesl." : "Maps" }}
          </span>
          <span class="truncate">{{ item.label }}</span>
        </li>
      </template>
    </ul>

    <LocationMapModal
      v-if="mapLocation"
      :inline="usesInlineLocationMap()"
      :location="mapLocation"
      @saved="onMapSaved"
      @close="closeMap"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue"
import LocationMapModal from "@/components/mention/LocationMapModal.vue"
import { useMentionEditor } from "@/composables/mention/use-mention"

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  autoFocus?: boolean
  rows?: number | string
  transformClipboard?: (value: string) => string
}>()

const emit = defineEmits<{ input: [event: Event]; "file-trigger": [] }>()
const text = defineModel<string | null | undefined>({ default: "" })

const attrs = useAttrs()
const forwardedClass = computed(() => attrs.class)
const placeholderText = computed(() => (attrs.placeholder as string | undefined) ?? "")
const isDisabled = computed(() => !!attrs.disabled)
const minHeight = computed(() => {
  const rows = Number(props.rows ?? 1)
  return `${(Number.isFinite(rows) && rows > 0 ? rows : 1) * 1.5 + 0.5}rem`
})
const editableAttrs = computed(() => {
  const forwarded = { ...(attrs as Record<string, unknown>) }
  delete forwarded.class
  delete forwarded.placeholder
  delete forwarded.rows
  delete forwarded.autosize
  delete forwarded.autoFocus
  delete forwarded.transformClipboard
  return forwarded
})

const {
  MIN_CHARS,
  rootEl,
  editorEl,
  isFocused,
  open,
  loading,
  items,
  atItems,
  atMentionMode,
  selectorMode,
  highlightedIndex,
  selectorTop,
  trimmedQuery,
  mapLocation,
  closeMap,
  onMapSaved,
  usesInlineLocationMap,
  handleInput,
  handleKeydown,
  handlePaste,
  handleEditorClick,
  handleEditorDblClick,
  handleFocusout,
  selectSuggestion,
  selectAtSuggestion,
  focus,
  insertUploadedFile
} = useMentionEditor({
  text,
  autoFocus: props.autoFocus,
  transformClipboard: props.transformClipboard,
  onInput: (event) => emit("input", event),
  onFileTrigger: () => emit("file-trigger")
})

defineExpose({
  focus,
  blur: () => editorEl.value?.blur(),
  updateSize: () => {},
  insertUploadedFile
})
</script>
